import {
  LyImageCropper,
  ImgCropperConfig,
  ImgCropperEvent,
  ImgCropperErrorEvent,
  ImgCropperLoaderConfig,
  ImgResolution,
  ImgCropperError,
} from '@alyle/ui/image-cropper';
import { LySliderChange } from '@alyle/ui/slider';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ImageCroppedEvent } from 'ngx-image-cropper';
import { Subscription } from 'rxjs';
import { AuthenticatedUserService } from 'src/app/core/services/authenticated-user/authenticated-user.service';
import { AdministratorDTO } from 'src/app/shared/models/dtos/responses/administrator-dto/administrator-dto';

@Component({
  selector: 'app-image-upload-modal',
  templateUrl: './image-upload-modal.component.html',
  styleUrls: ['./image-upload-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
/**
 * Image Upload Molecule
 */
export class ImageUploadModalComponent implements OnInit, AfterViewInit {
  @Input() fileUploadLabel!: string[];
  @Input() fileUploadAlt!: string[];
  control: AbstractControl | null | undefined;
  @Output() fileInput = new EventEmitter<any>();
  @ViewChild(LyImageCropper) cropper!: LyImageCropper;
  selectedFile!: string;
  fileUrl?: string | '';
  imageError = false;
  showCropper: boolean = false;
  loading: boolean = false;
  imageChangedEvent: null | Event = null;
  private subsink = new Subscription();
  croppedImage?: string | '';
  scale = 1;
  ready = false;
  minScale!: number;
  admin?: AdministratorDTO;
  modalTitle: string = 'change-account-image';
  errorMessageKey = '';

  initConfig: ImgCropperConfig = {
    width: 250,
    height: 250,
    responsiveArea: true,
    round: true,
    output: ImgResolution.Default,
    maxFileSize: 2000000, // 2 MB
  };

  config: ImgCropperLoaderConfig = {
    originalDataURL: '',
  };

  minSizeEvent: ImgCropperErrorEvent = {
    name: undefined,
    type: 'image/jpeg',
    size: NaN,
    error: 0,
  };

  /**
   * set up file url if avaiable from form control
   */
  ngOnInit() {
    this.imageError = false;
    this.fileUrl = '';
    const formControl = this.getFormControlFromData();
    if (formControl instanceof FormControl) {
      this.control = formControl;
      this.fileUrl = this.control.value;
    }
    if (this.control instanceof AbstractControl) {
      this.fileUrl = this.control.value;
      this.subsink.add(
        this.control?.valueChanges.subscribe((value) => {
          this.fileUrl = value;
        })
      );
    }

    this.setupModalTitle();

    this.subsink.add(
      this.authenticatedUserService.admin$.subscribe((info) => {
        this.admin = info;
      })
    );
  }

  /**
   * Constructor to import translation service
   */
  constructor(
    private readonly authenticatedUserService: AuthenticatedUserService,
    public dialogRef: MatDialogRef<ImageUploadModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ImageUploadModalComponent
  ) {}

  /**
   * set up initial config
   */
  ngAfterViewInit() {
    if (typeof this.fileUrl === 'string') {
      const url = this.fileUrl;
      const config: ImgCropperLoaderConfig = {
        scale: 0.745864772531767,
        xOrigin: 642.380608078103,
        yOrigin: 236.26357452128866,
        rotation: 0,
        originalDataURL: url,
      };
      this.loading = false;
    }
  }

  /**
   * Image on load
   * @param {ImgCropperEvent} e object
   */
  onLoaded(e: ImgCropperEvent) {
    this.imageError = false;
    this.errorMessageKey = '';
    this.croppedImage = e.originalDataURL;
  }

  /**
   * Image on crop
   * @param {ImgCropperEvent} e object
   */
  onCropped(e: ImgCropperEvent) {
    this.fileUrl = e.dataURL;
  }

  /**
   * Image on error
   * @param {ImgCropperEvent} e object
   */
  onError(e: ImgCropperErrorEvent) {
    this.imageError = true;
    this.ready = false;
    switch (e.error) {
      case ImgCropperError.Size:
        this.errorMessageKey = 'image-is-too-big';
        break;
      case ImgCropperError.Type:
        this.errorMessageKey = 'invalid-image-type';
        break;
      case ImgCropperError.Other:
      default:
        this.errorMessageKey = 'image-error-generic';
        break;
    }
  }

  /**
   * Image on slider change
   * @param {LySliderChange} event object
   */
  onSliderInput(event: LySliderChange) {
    this.scale = event.value as number;
  }

  /**
   * @param {HTMLImageElement} image
   * @return {boolean}
   */
  isImageValid(image: HTMLImageElement | ImageCroppedEvent): boolean {
    const min = 192; // px
    return image.width >= min && image.height >= min;
  }

  /**
   * Cleans up resources
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * Cancels crop and revert image, if exists
   */
  cancelCrop() {
    if (this.selectedFile) {
      this.croppedImage = this.selectedFile;
    }
    this.showCropper = false;
  }

  /**
   * Action to close the dialog
   */
  onSuccessClick() {
    this.cropper.crop();
    this.showCropper = false;
    this.imageChangedEvent = null;
    this.dialogRef.close(this.fileUrl);
  }

  /**
   * Action to close the dialog
   */
  onFailClick() {
    this.dialogRef.close();
  }

  /**
   * Check if this.data is a formControl or has a formControl and return it
   * @return {FormControl | null}
   */
  getFormControlFromData(): FormControl | null {
    if (this.data instanceof FormControl) {
      return this.data;
    }

    if (this.data?.control && this.data?.control instanceof FormControl) {
      return this.data.control;
    }

    return null;
  }

  /**
   * Check if modalTitle was passed and save it to this.modalTitle
   */
  setupModalTitle() {
    if (this.data && this.data?.modalTitle) {
      this.modalTitle = this.data?.modalTitle;
    }
  }
}
